/**
 * The one open card peek.
 *
 * Only one CardName is open at a time across the whole page. A peek that
 * opens (previewing or pinned) claims the slot, which closes whichever peek
 * held it. A ModalShell opening (the card lightbox, the combo viewer) closes
 * the active peek too, so a peek never floats over a modal that replaced it.
 */
interface ActivePeek {
  id: string
  close: () => void
}

let active: ActivePeek | null = null

/**
 * Bumped whenever the slot changes hands or a modal clears it. A preview
 * that is still WAITING to open (useCardHoverPreview's 150ms hover delay)
 * holds no claim, so closeActivePeek cannot close it; it captures this
 * number when it schedules and opens only if nothing happened since.
 */
let generation = 0

/** The current generation (see above). */
export function peekGeneration(): number {
  return generation
}

/** Make `id` the open peek, closing the previous one if it was another. */
export function claimPeek(id: string, close: () => void): void {
  if (!active || active.id !== id) generation++
  if (active && active.id !== id) {
    const previous = active
    active = null
    previous.close()
  }
  active = { id, close }
}

/** Give the slot back, if `id` still holds it. */
export function releasePeek(id: string): void {
  if (active?.id === id) active = null
}

/** Close whatever peek is open (a modal is about to cover the page). */
export function closeActivePeek(): void {
  // Even with nothing open: a pending hover open must not land over the
  // modal that is about to cover the page.
  generation++
  if (!active) return
  const previous = active
  active = null
  previous.close()
}

export function activePeekId(): string | null {
  return active?.id ?? null
}

/** Test seam. */
export function _resetPeekStoreForTests(): void {
  active = null
}
